import { Inject, Injectable } from '@angular/core';
import { AppConfigService } from '@core/appconfig.service';
import { AuthenticationService } from '@core/authentication.service';
import { ProductService } from '@core/product.service';
import { UserService } from '@core/user.service';
import { Ebook2026ReadyMessage, ZBPortalMessageType, } from '@services/message-event/message-event.model';
import { RoleType } from '@shared/enums/role-type';
import { UIHelpers } from '@shared/uihelpers';
import { WINDOW } from '../../../window';

@Injectable({
  providedIn: 'root'
})
export class MessageEventService {

  /** RoleTypes that are allowed to handle messages. */
  public static readonly ALLOWED_ROLES: RoleType[] = [RoleType.Teacher, RoleType.Student];

  /** Cache for {@link cdnOrigin}. */
  private _trimmedAssetUrl: string | undefined;
  private _listener: ((event: MessageEvent) => void) | undefined;

  constructor(
    private userService: UserService,
    private productService: ProductService,
    private authService: AuthenticationService,
    private appConfigService: AppConfigService,
    @Inject(WINDOW) private zbpPortalWindow: Window,
  ) {
  }

  private get cdnOrigin(): string {
    if (!this._trimmedAssetUrl) {
      // window origins do not have trailing slashes, so trim the CDN url.
      this._trimmedAssetUrl = this.appConfigService.assetUrl.endsWith('/')
        ? this.appConfigService.assetUrl.slice(0, -1)
        : this.appConfigService.assetUrl;
    }
    return this._trimmedAssetUrl;
  }

  /**
   * Called by the root component onInit
   */
  startListening(): void {
    // clear out any previous listeners to avoid duplicate responses
    this.stopListening();
    this._listener = this.onMessageEvent.bind(this);
    this.zbpPortalWindow.addEventListener('message', this._listener);
  }

  stopListening(): void {
    if (this._listener) {
      this.zbpPortalWindow.removeEventListener('message', this._listener);
    }
  }

  /**
   * This reset method is called by the stateful services manager service
   */
  reset(): void {
    this.stopListening();
  }

  /** Posts a message to the iframe to trigger printing. */
  postMessageToPdfViewerToPrint(iframeWindow: HTMLIFrameElement): void {
    iframeWindow?.contentWindow?.postMessage(
      { type: ZBPortalMessageType.PrintPdfOnLoad },
      { targetOrigin: this.appConfigService.getOrigin(false) },
    );
  }

  /** Handle all messages events for ZB Portal. */
  private onMessageEvent(event: MessageEvent): void {
    console.debug('[MESSAGE]', event?.data, event);
    if (!this.isViewingAsAllowedRole) {
      // if not viewing as allowed role(s), do not handle messages
      return;
    }
    if (this.isPrintPdfCompleteEvent(event)) {
      this.handlePrintPdfCompleteEvent();
    } else if (this.isEbook2026Message(event)) {
      this.handleEbook2026ReadyMessage(event, event.data.assetId);
    }
  }

  private get isViewingAsAllowedRole(): boolean {
    return this.userService.user && this.userService.user.isViewingAsRoleGroup(MessageEventService.ALLOWED_ROLES);
  }

  private isPrintPdfCompleteEvent(event: MessageEvent): boolean {
    return event.data?.type === ZBPortalMessageType.PrintPdfComplete
      && event.origin === this.appConfigService.getOrigin(false);
  }

  private handlePrintPdfCompleteEvent(): void {
    UIHelpers.closeWindow(false);
  }

  private isEbook2026Message(event: MessageEvent): boolean {
    // ebook messages may come from CDN (teacher experience) or same origin iFrame (student experience).
    return event.data?.type === ZBPortalMessageType.Ebook2026Ready
      && (event.origin === this.cdnOrigin
        || event.origin === this.appConfigService.getOrigin(false));
  }

  /**
   * If the license is valid for the asset we'll send a message with the token. Otherwise,
   * we'll return without sending the token.
   * */
  private handleEbook2026ReadyMessage(event: MessageEvent<Ebook2026ReadyMessage>, assetId: string): void {
    this.productService.determineLicenseValid(assetId).subscribe((res) => {
      if (res.success && res.response.length > 0) {
        const ebookWindow = event.source as Window;
        ebookWindow.postMessage(
          { type: 'token', token: this.authService.apiToken },
          { targetOrigin: event.origin },
        );
      }
    });
  }
}
